import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from "../../config";
import { showToast } from '../../components/toast';
import LoadingSpinner from '../../components/LoadingSpinner';

const EditSitemap = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [formData, setFormData] = useState({
    pageName: '',
    pageUrl: '',
    productApp: '0' // Default to streetgains
  });

  // Fetch existing sitemap data
  const fetchSitemapData = async () => {
    try {
      setFetchLoading(true);
      const token = localStorage.getItem('authToken');
      
      console.log('Fetching data for ID:', id);
      
      // Using the same endpoint as the list page but with specific ID
      const response = await axios.get(
        `${API_URL}siteMap/sitemap?limit=1000&page=1`, // Get all records to find the specific one
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log('API Response:', response.data);

      if (response.data && response.data.success) {
        const allSitemaps = response.data.data.sitemap;
        
        if (Array.isArray(allSitemaps)) {
          // Find the specific sitemap by ID
          const sitemapData = allSitemaps.find(item => item.id === parseInt(id));
          console.log('Found sitemap data:', sitemapData);
          
          if (sitemapData) {
            setFormData({
              pageName: sitemapData.pageName || sitemapData.page_name || '',
              pageUrl: sitemapData.pageUrl || sitemapData.page_url || '',
              productApp: sitemapData.product_app?.toString() || '0'
            });
            console.log('Form data set:', {
              pageName: sitemapData.pageName || sitemapData.page_name || '',
              pageUrl: sitemapData.pageUrl || sitemapData.page_url || '',
              productApp: sitemapData.product_app?.toString() || '0'
            });
          } else {
            showToast.error('Sitemap not found');
            navigate('/Website/Sitemap');
          }
        } else {
          showToast.error('Invalid data format received');
          navigate('/Website/Sitemap');
        }
      } else {
        const errorMessage = response.data?.message || 'Failed to fetch sitemap data';
        showToast.error(errorMessage);
        navigate('/Website/Sitemap');
      }
    } catch (error) {
      console.error('Error fetching sitemap data:', error);
      console.error('Error response:', error.response?.data);
      
      // Try alternative endpoint if the first one fails
      try {
        const token = localStorage.getItem('authToken');
        const alternativeResponse = await axios.get(
          `${API_URL}siteMap/sitemap/form?id=${id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Accept': '*/*'
            },
          }
        );
        
        console.log('Alternative API Response:', alternativeResponse.data);
        
        if (alternativeResponse.data) {
          const sitemapData = alternativeResponse.data.sitemap || alternativeResponse.data;
          setFormData({
            pageName: sitemapData.pageName || sitemapData.page_name || '',
            pageUrl: sitemapData.pageUrl || sitemapData.page_url || '',
            productApp: sitemapData.product_app?.toString() || '0'
          });
        }
      } catch (altError) {
        const errorMessage = error.response?.data?.message || 'Failed to fetch sitemap data. Please try again.';
        showToast.error(errorMessage);
        navigate('/Website/Sitemap');
      }
    } finally {
      setFetchLoading(false);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    if (id) {
      fetchSitemapData();
    } else {
      navigate('/Website/Sitemap');
    }
  }, [id, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form data
    if (!formData.pageName.trim()) {
      showToast.error('Please enter page name');
      return;
    }
    
    if (!formData.pageUrl.trim()) {
      showToast.error('Please enter page URL');
      return;
    }

    // Basic URL validation
    try {
      new URL(formData.pageUrl);
    } catch (error) {
      showToast.error('Please enter a valid URL');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      
      const requestData = {
        id: parseInt(id),
        page_name: formData.pageName.trim(),
        page_url: formData.pageUrl.trim(),
        product_app: parseInt(formData.productApp) // Include product_app in update
      };
      
      console.log('Updating with data:', requestData);
      
      const response = await axios.post(
        `${API_URL}siteMap/save`,
        requestData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      console.log('Update response:', response.data);

      if (response.data && response.data.success) {
        showToast.success('Sitemap page updated successfully');
        // Navigate back to sitemap list
        navigate('/Website/Sitemap');
      } else {
        const errorMessage = response.data?.message || 'Failed to update sitemap page';
        showToast.error(errorMessage);
      }
    } catch (error) {
      console.error('Error updating sitemap page:', error);
      console.error('Update error response:', error.response?.data);
      const errorMessage = error.response?.data?.message || 'Failed to update sitemap page. Please try again.';
      showToast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/Website/Sitemap');
  };

  if (fetchLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="page-container mt-5">
      {loading && <LoadingSpinner />}
      
      {/* Breadcrumb */}
      <div className="d-flex align-items-center mb-3">
        <Link to="/" className="text-primary text-decoration-none">
          <i className="fas fa-home me-1"></i> Home
        </Link>
        <span className="mx-2">/</span>
        <Link to="/Website/Sitemap" className="text-primary text-decoration-none">
          Manage Sitemap
        </Link>
        <span className="mx-2">/</span>
        <span>Edit Page</span>
      </div>

      <div className="panel panel-default with-rounded-corner with-shadow p-10">
        <div className="panel-body">
          <h3 className="mb-4">Edit Sitemap Page</h3>
          
          <form onSubmit={handleSubmit}>
            <div className="row mb-3">
              <div className="col-md-4">
                <label className="form-label">
                  Page Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="pageName"
                  value={formData.pageName}
                  onChange={handleInputChange}
                  className="form-control"
                  placeholder="Enter Page Name"
                  disabled={loading}
                  required
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">
                  Page URL <span className="text-danger">*</span>
                </label>
                <input
                  type="url"
                  name="pageUrl"
                  value={formData.pageUrl}
                  onChange={handleInputChange}
                  className="form-control"
                  placeholder="Enter Page URL (e.g., https://example.com/page)"
                  disabled={loading}
                  required
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">
                  Sitemap Type <span className="text-danger">*</span>
                </label>
                <select
                  name="productApp"
                  value={formData.productApp}
                  onChange={handleInputChange}
                  className="form-control"
                  disabled={loading}
                  required
                >
                  <option value="0">Streetgains Sitemap</option>
                  <option value="1">Streetfolio Sitemap</option>
                </select>
              </div>
            </div>

            <div className="d-flex gap-2 mt-4">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Updating...
                  </>
                ) : (
                  'Update'
                )}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="btn btn-danger"
                disabled={loading}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Scroll to top button */}
      <a href="#" data-click="scroll-top" className="btn-scroll-top fade">
        <i className="ti-arrow-up"></i>
      </a>
    </div>
  );
};

export default EditSitemap;